import { useEffect, useRef } from 'react'
import { connectSocket, disconnectSocket } from '../lib/socket-client.js'
import { useAuthStore } from '../stores/useAuthStore.js'
import { useSocketStore } from '../stores/useSocketStore.js'
import { useChatStore } from '../stores/useChatStore.js'
import { SERVER_EVENTS } from '../types/socket-events.js'
import type {
  NewMessageEvent,
  MessageUpdatedEvent,
  MessageDeletedEvent,
  ReadReceiptEvent,
} from '../types/socket-events.js'

export function useSocket() {
  const tokens = useAuthStore((s) => s.tokens)
  const user = useAuthStore((s) => s.user)
  const setConnected = useSocketStore((s) => s.setConnected)
  const addMessage = useChatStore((s) => s.addMessage)
  const updateMessage = useChatStore((s) => s.updateMessage)
  const removeMessage = useChatStore((s) => s.removeMessage)
  const addReadReceipt = useChatStore((s) => s.addReadReceipt)
  const initialized = useRef(false)

  useEffect(() => {
    if (!tokens || !user || initialized.current) return
    initialized.current = true

    const socket = connectSocket()

    socket.on('connect', () => setConnected(true))
    socket.on('disconnect', () => setConnected(false))

    socket.on(SERVER_EVENTS.NEW_MESSAGE, (event: NewMessageEvent) => {
      addMessage(event.chatId, event.message as unknown as import('../types/chat.js').ChatMessage)
    })

    socket.on(SERVER_EVENTS.MESSAGE_UPDATED, (event: MessageUpdatedEvent) => {
      updateMessage(event.chatId, event.messageId, {
        content: event.content,
        editedAt: event.editedAt,
      })
    })

    socket.on(SERVER_EVENTS.MESSAGE_DELETED, (event: MessageDeletedEvent) => {
      removeMessage(event.chatId, event.messageId)
    })

    socket.on(SERVER_EVENTS.READ_RECEIPT, (event: ReadReceiptEvent) => {
      addReadReceipt(event.chatId, event.messageId, event.userId)
    })

    return () => {
      initialized.current = false
      disconnectSocket()
      setConnected(false)
    }
  }, [tokens, user, setConnected, addMessage, updateMessage, removeMessage, addReadReceipt])
}
